import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { Wordmark } from "@/components/BrandMark";
import { ThemeToggle } from "@/components/ThemeToggle";
import { toast } from "sonner";

export default function Login() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("signup") ? "signup" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [betaCode, setBetaCode] = useState("");
  const [busy, setBusy] = useState(false);
  const { user, loginPassword, registerPassword } = useAuth();
  const nav = useNavigate();

  useEffect(() => { if (user) nav("/app"); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup") {
        const res = await registerPassword(email, password, name, betaCode.trim());
        toast.success(res?.beta ? "Beta access unlocked — welcome!" : "Welcome to Dynasty Metrics");
        const ref = params.get("ref");
        if (ref) {
          try {
            const rr = await api.post("/referral/redeem", { code: ref });
            toast.success(`Referral applied — ${rr.data.reward_days} days of Pro free 🎉`);
          } catch (e) { console.debug("referral redeem skipped", e); }
        }
      } else {
        await loginPassword(email, password);
        toast.success("Welcome back");
      }
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Auth failed");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex relative">
      <div className="absolute top-4 right-4 z-10"><ThemeToggle /></div>
      <div className="hidden md:flex w-1/2 relative items-end p-12 terminal-grid border-r border-[var(--border)]">
        <div className="absolute inset-0 bg-gradient-to-tr from-black/5 via-transparent to-transparent" />
        <div className="relative">
          <Wordmark size={40} sub="DYNASTY ANALYTICS" className="mb-6" />
          <h2 className="font-display text-4xl font-black uppercase tracking-tight max-w-md leading-[0.95]">
            Your dynasty,<br /><span className="text-[var(--accent)]">quantified.</span>
          </h2>
          <div className="text-[var(--text-2)] mt-4 max-w-md text-sm">
            7-day free trial. Cancel anytime. No credit card to start.
          </div>
        </div>
      </div>

      <div className="w-full md:w-1/2 flex items-center justify-center p-6">
        <div className="w-full max-w-sm fade-up">
          <div className="font-display text-3xl font-bold mb-1" data-testid="auth-heading">
            {mode === "signup" ? "Create your account" : "Welcome back"}
          </div>
          <div className="text-sm text-[var(--text-2)] mb-8">
            {mode === "signup" ? "Start your 7-day free trial." : "Log in to your terminal."}
          </div>

          <button onClick={googleLogin} className="btn-secondary w-full mb-3 flex items-center justify-center gap-2" data-testid="google-btn">
            <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.4 0-11.5-5.1-11.5-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.7 2.9l5.7-5.7C33.6 6.3 29 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c2.9 0 5.6 1.1 7.7 2.9l5.7-5.7C33.6 6.3 29 4.5 24 4.5 16.3 4.5 9.7 8.8 6.3 14.7z"/><path fill="#4CAF50" d="M24 43.5c4.9 0 9.5-1.9 12.9-5l-6-5c-1.8 1.4-4.2 2.5-6.9 2.5-5.3 0-9.7-3.1-11.3-7.5L6.3 33C9.7 38.7 16.3 43 24 43.5z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.4 4.3-4.4 5.5l6 5C40.7 36.4 43.5 31 43.5 24c0-1.2-.1-2.4-.4-3.5z"/></svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-4 text-xs text-[var(--text-3)]">
            <div className="flex-1 h-px bg-[var(--border)]" /> OR <div className="flex-1 h-px bg-[var(--border)]" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "signup" && (
              <input className="input-dark" placeholder="Display name" value={name} onChange={e => setName(e.target.value)} data-testid="signup-name" />
            )}
            <input className="input-dark" type="email" required placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} data-testid="auth-email" />
            <input className="input-dark" type="password" required placeholder="Password" minLength={6} value={password} onChange={e => setPassword(e.target.value)} data-testid="auth-password" />
            {mode === "signup" && (
              <div>
                <input className="input-dark font-mono" placeholder="Beta code (optional)" value={betaCode} onChange={e => setBetaCode(e.target.value)} data-testid="beta-code-input" />
                <div className="text-[11px] text-[var(--text-3)] mt-1.5 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] animate-pulse" />
                  Got a beta code? Enter it to skip the card and unlock everything free.
                </div>
              </div>
            )}
            <button disabled={busy} className="btn-primary w-full flex items-center justify-center" data-testid="auth-submit">
              {busy ? <Loader2 className="w-4 h-4 spin" /> : mode === "signup" ? "Start Free" : "Log in"}
            </button>
          </form>

          <div className="text-sm text-center mt-6 text-[var(--text-2)]">
            {mode === "signup" ? "Already have an account?" : "New here?"}{" "}
            <button onClick={() => setMode(mode === "signup" ? "login" : "signup")} className="text-[var(--accent)] hover:underline" data-testid="auth-switch">
              {mode === "signup" ? "Log in" : "Create an account"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
